export interface BookingAvailabilitySlot {
  available: boolean;
  dayOffset: number;
  end: string;
  endLocal?: string;
  endUtc?: string;
  start: string;
  startLocal?: string;
  startUtc?: string;
}

export const BOOKING_SLOT_MINUTES = 30;
export const BOOKING_DURATION_OPTIONS = [30, 60, 90, 120] as const;

const HH_MM = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

function timeMinutes(value: string): number | null {
  if (value === "24:00") return 24 * 60;
  if (!HH_MM.test(value)) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

export function formatAvailabilityDate(date: Date): string | null {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatAvailabilityTime(date: Date): string | null {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function normalizeBookingAvailability(
  response: unknown,
): BookingAvailabilitySlot[] {
  const envelope = response as {
    data?: { timeSlots?: unknown };
    timeSlots?: unknown;
  };
  const rows = Array.isArray(envelope?.data?.timeSlots)
    ? envelope.data.timeSlots
    : Array.isArray(envelope?.timeSlots)
      ? envelope.timeSlots
      : [];

  return rows.flatMap((row): BookingAvailabilitySlot[] => {
    const candidate = row as {
      available?: unknown;
      dayOffset?: unknown;
      end?: unknown;
      endLocal?: unknown;
      endUtc?: unknown;
      start?: unknown;
      startLocal?: unknown;
      startUtc?: unknown;
    };
    const start = typeof candidate.start === "string" ? candidate.start : "";
    const end = typeof candidate.end === "string" ? candidate.end : "";
    if (timeMinutes(start) == null || timeMinutes(end) == null) return [];
    return [
      {
        available: candidate.available === true,
        dayOffset: Number(candidate.dayOffset) || 0,
        end,
        endLocal:
          typeof candidate.endLocal === "string"
            ? candidate.endLocal
            : undefined,
        endUtc:
          typeof candidate.endUtc === "string" ? candidate.endUtc : undefined,
        start,
        startLocal:
          typeof candidate.startLocal === "string"
            ? candidate.startLocal
            : undefined,
        startUtc:
          typeof candidate.startUtc === "string"
            ? candidate.startUtc
            : undefined,
      },
    ];
  });
}

export function bookingAvailabilitySlotKey(
  slot: BookingAvailabilitySlot,
): string {
  return slot.startUtc || slot.startLocal || slot.start;
}

function slotRange(slot: BookingAvailabilitySlot): {
  end: number;
  start: number;
} | null {
  if (slot.startUtc && slot.endUtc) {
    const start = Date.parse(slot.startUtc);
    const end = Date.parse(slot.endUtc);
    return Number.isFinite(start) && Number.isFinite(end) && end > start
      ? { start, end }
      : null;
  }
  const start = timeMinutes(slot.start);
  const rawEnd = timeMinutes(slot.end);
  if (start == null || rawEnd == null) return null;
  const end = rawEnd <= start ? rawEnd + 24 * 60 : rawEnd;
  return end > start ? { start, end } : null;
}

export function getAvailableBookingDurations(
  slots: BookingAvailabilitySlot[],
  selectedStart: string,
): number[] {
  const startIndex = slots.findIndex(
    (slot) =>
      bookingAvailabilitySlotKey(slot) === selectedStart && slot.available,
  );
  if (startIndex < 0) return [];

  const firstRange = slotRange(slots[startIndex]);
  if (!firstRange) return [];

  let contiguousEnd = firstRange.start;
  for (let index = startIndex; index < slots.length; index += 1) {
    const slot = slots[index];
    const range = slotRange(slot);
    if (!slot.available || !range || range.start !== contiguousEnd) {
      break;
    }
    contiguousEnd = range.end;
  }

  const divisor = slots[startIndex].startUtc ? 60_000 : 1;
  const availableMinutes = (contiguousEnd - firstRange.start) / divisor;
  return BOOKING_DURATION_OPTIONS.filter(
    (duration) => duration <= availableMinutes,
  );
}

export function applyBookingSlot(
  selectedDate: Date,
  selectedStart: string,
  durationMinutes: number,
  slots: BookingAvailabilitySlot[] = [],
): { end: Date; start: Date } | null {
  if (!BOOKING_DURATION_OPTIONS.some((option) => option === durationMinutes)) {
    return null;
  }
  const selectedSlot = slots.find(
    (slot) => bookingAvailabilitySlotKey(slot) === selectedStart,
  );
  if (selectedSlot?.startUtc) {
    const start = new Date(selectedSlot.startUtc);
    if (!Number.isFinite(start.getTime())) return null;
    return {
      start,
      end: new Date(start.getTime() + durationMinutes * 60_000),
    };
  }
  const minutes = timeMinutes(selectedStart);
  if (minutes == null || minutes >= 24 * 60) {
    return null;
  }

  const start = new Date(selectedDate);
  if (!Number.isFinite(start.getTime())) return null;
  start.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return {
    start,
    end: new Date(start.getTime() + durationMinutes * 60_000),
  };
}
